import { CircularProgress } from '@material-ui/core';
import { Link, SolvTheme, Stack, style, Text } from '@solvhealth/jigsaw-v1';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import styled from 'styled-components';
import { colors } from '~/constants/colors';
import {
  getCityStateZipStr,
  getFullNameFromSnakeCaseProps,
  getPharmacyParts,
  getProfileCompletionPercentage,
  hasAddressInfo,
  hasPharmacyInfo,
  hasProfilePhoto,
} from '~/core/util/account';

import { analyticsTrackEvent } from '~/core/analytics';
import { ACCOUNT_PROFILE_TOUCH_EDIT } from '~/core/analytics/events';
import { UserProfile } from '~/core/dapi/models';
import { capitalize } from '~/core/util/string';
import DateTime, { MONTH_ABBV_DAY_YEAR } from '../../../DateTime';
import Avatar from '../Avatar';
import { ProfileModalTypes } from './constants';
import EditProfileModal from './EditProfileModal';
import ExifImageLoader from '~/core/util/ExifImageLoader';
import { fontSize } from '~/constants/text';
import FileUpload from '~/components/SolvPatternLibrary/FileUpload';

const MainContent = styled.div.withConfig({ componentId: 'sc-kaf4rq-0' })`
  display: flex;
  padding-bottom: 6px;
`;

const LeftRail = styled.div.withConfig({ componentId: 'sc-kaf4rq-1' })`
  padding: 20px 16px;
  text-align: center;
`;

const RightRail = styled.div.withConfig({ componentId: 'sc-kaf4rq-2' })`
  width: 100%;
`;

const SubContent = styled.div.withConfig({ componentId: 'sc-kaf4rq-3' })`
  border-bottom: 1px solid ${colors.borderLine};
  margin-right: 20px;
  padding: 20px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const SubContentHeader = styled.div.withConfig({ componentId: 'sc-kaf4rq-4' })`
  display: flex;
  justify-content: space-between;
`;

const StyledAvatar = styled(Avatar).withConfig({ componentId: 'sc-kaf4rq-5' })`
  cursor: pointer;
  height: 90px;
  width: 90px;
  display: flex;
  font-size: 40px;
  margin-bottom: 6px;
`;

const UploadPhotoButton = styled.label.withConfig({ componentId: 'sc-kaf4rq-6' })`
  font-size: ${fontSize.mediumSmall};
  font-weight: 400;
  color: ${colors.crunchBerry};
  text-align: center;
  cursor: pointer;
`;

const PhotoInput = styled(FileUpload).withConfig({ componentId: 'sc-kaf4rq-7' })`
  position: absolute;
  opacity: 0;
  z-index: -1;
  font-size: 0;
`;

type Props = {
  colorTheme?: object;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  handleCropSave: func;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  updateUserProfile: func;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  updateUserProfileProcessing?: func;
  userProfile?: UserProfile;
  userProfilePhoto?: object;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  openSnackBar: func;
  // @ts-expect-error ts-migrate(2749) FIXME: 'array' refers to a value, but is being used as a ... Remove this comment to see the full error message
  pharmacies?: array;
  pharmacy?: object;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  searchPharmacies?: func;
  profileColor?: string;
  position?: object;
  // @ts-expect-error ts-migrate(2749) FIXME: 'func' refers to a value, but is being used as a t... Remove this comment to see the full error message
  clearPharmacies?: func;
};

type State = any;

class ProfileCard extends Component<Props, State> {
  state = { src: '', open: false, modalToOpen: undefined };

  componentDidUpdate = (prevProps: any, prevState: any) => {
    const { clearPharmacies } = this.props;

    if (!isEqual(prevProps.userProfile, this.props.userProfile)) {
      setTimeout(this.props.openSnackBar, 1000);
    }

    if (prevState && prevState.open && !this.state.open && typeof clearPharmacies === 'function') {
      clearPharmacies();
    }
  };

  selectFile = (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const imageLoader = new ExifImageLoader(e.target.files[0]);
      imageLoader.getDataURL(false).then((image) => {
        this.setState({
          open: true,
          modalToOpen: ProfileModalTypes.PHOTO,
          src: image as unknown as string,
        });
      });
    }
  };

  openModal = (modalType: any) => () => {
    analyticsTrackEvent(ACCOUNT_PROFILE_TOUCH_EDIT, {
      action: 'open',
      section: modalType,
    });
    this.setState({ open: true, modalToOpen: modalType });
  };

  closeModal = () => this.setState({ open: false });

  render = () => {
    const { src, open, modalToOpen } = this.state;
    const {
      profileColor,
      userProfile,
      handleCropSave,
      updateUserProfile,
      updateUserProfileProcessing,
      userProfilePhoto,
      pharmacies,
      pharmacy,
      searchPharmacies,
      position,
    } = this.props;

    const fullName = getFullNameFromSnakeCaseProps(userProfile);
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 2-3 arguments, but got 1.
    const birthDate = DateTime.format(userProfile.birth_date)(MONTH_ABBV_DAY_YEAR);
    const patientSex = capitalize(userProfile?.birth_sex);
    const cityStateZipStr = getCityStateZipStr(userProfile);
    const pharmacyParts = getPharmacyParts(userProfile?.pharmacy);
    const percentageComplete = getProfileCompletionPercentage(userProfile);

    return (
      <Stack bg="white" border="solid 1px" borderColor="gray-200" borderRadius="base">
        <Stack alignItems="center" justifyContent="space-between" p={3} row>
          <Text variant="h3">Profile</Text>

          <Stack alignItems="center" row space={1}>
            <Text textColor="crunchberry" variant="body2">
              {percentageComplete}% complete
            </Text>
            <Stack position="relative">
              <CircularProgress
                color="inherit"
                size={SolvTheme.space[3]}
                style={style({ textColor: 'gray-200', zIndex: 0 })}
                thickness={5}
                value={100}
                variant="determinate"
              />
              <CircularProgress
                color="inherit"
                size={SolvTheme.space[3]}
                style={style({ textColor: 'crunchberry', position: 'absolute', zIndex: 10 })}
                thickness={5}
                value={percentageComplete}
                variant="determinate"
              />
            </Stack>
          </Stack>
        </Stack>

        <MainContent>
          <LeftRail>
            <UploadPhotoButton htmlFor="photoInput">
              <StyledAvatar profileColor={profileColor} userProfile={userProfile} />
            </UploadPhotoButton>
            <PhotoInput accept="image/*" id="photoInput" onChange={this.selectFile} type="file" />

            <UploadPhotoButton htmlFor="photoInput">
              {hasProfilePhoto(userProfile) ? 'Change' : 'Add'}
            </UploadPhotoButton>
          </LeftRail>

          <RightRail>
            <SubContent>
              <SubContentHeader>
                <Text variant="subtitle2">Profile basics</Text>

                <Link onClick={this.openModal(ProfileModalTypes.BASICS)} size="small">
                  Edit
                </Link>
              </SubContentHeader>

              <Text variant="body2">{fullName}</Text>

              <Text muted variant="body2">
                Born&nbsp;
                {birthDate}
              </Text>

              <Text muted variant="body2">
                {patientSex}
              </Text>
            </SubContent>

            <SubContent>
              <SubContentHeader>
                <Text variant="subtitle2">Home address</Text>

                <Link onClick={this.openModal(ProfileModalTypes.ADDRESS)} size="small">
                  {hasAddressInfo(userProfile) ? 'Edit' : 'Add'}
                </Link>
              </SubContentHeader>
              <Text muted variant="body2">
                {userProfile?.address_street}
              </Text>
              {userProfile?.address_street_secondary && (
                <Text muted variant="body2">
                  {userProfile.address_street_secondary}
                </Text>
              )}
              <Text muted variant="body2">
                {cityStateZipStr}
              </Text>
            </SubContent>

            <SubContent>
              <SubContentHeader>
                <Text variant="subtitle2">Preferred pharmacy</Text>

                <Link onClick={this.openModal(ProfileModalTypes.PHARMACY)} size="small">
                  {hasPharmacyInfo(userProfile) ? 'Edit' : 'Add'}
                </Link>
              </SubContentHeader>
              <Text muted variant="body2">
                {pharmacyParts.first}
              </Text>
              <Text muted variant="body2">
                {pharmacyParts.second}
              </Text>
            </SubContent>
          </RightRail>
        </MainContent>

        <EditProfileModal
          closeModal={this.closeModal}
          handleCropSave={handleCropSave}
          modalType={modalToOpen}
          open={open}
          pharmacies={pharmacies}
          pharmacy={pharmacy}
          position={position}
          profileColor={profileColor}
          searchPharmacies={searchPharmacies}
          src={src}
          updateUserProfile={updateUserProfile}
          updateUserProfileProcessing={updateUserProfileProcessing}
          userProfile={userProfile}
          userProfilePhoto={userProfilePhoto}
        />
      </Stack>
    );
  };
}

export default ProfileCard;
